import React from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Construction,
  Schedule,
  Timeline,
  NotificationsActive,
  Refresh,
  ArrowBack
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Medstown theme
const medstownTheme = createTheme({
  palette: {
    primary: {
      main: '#1d5244',
      light: '#4a7c59',
      dark: '#0f2920',
    },
    secondary: {
      main: '#2e7d32',
    },
    background: {
      default: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h3: {
      fontWeight: 700,
      fontSize: '2.5rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

// Floating Animation Component
const FloatingIcon = ({ children, delay = 0 }) => (
  <Box
    sx={{
      animation: 'float 3s ease-in-out infinite',
      animationDelay: `${delay}s`,
      '@keyframes float': {
        '0%': {
          transform: 'translateY(0px)',
        },
        '50%': {
          transform: 'translateY(-10px)',
        },
        '100%': {
          transform: 'translateY(0px)',
        },
      },
    }}
  >
    {children}
  </Box>
);

export default function ActivityLogs() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleGoBack = () => {
    window.history.back();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <ThemeProvider theme={medstownTheme}>
      <Box 
        sx={{ 
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        {/* Header */}
        <Paper 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bgcolor: 'primary.main', 
            color: 'white',
            borderRadius: 0,
            boxShadow: '0 4px 20px rgba(29, 82, 68, 0.3)',
            zIndex: 10
          }}
        >
          <Container maxWidth="xl">
            <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box display="flex" alignItems="center">
                <Timeline sx={{ mr: 2, fontSize: 32 }} />
                <Box>
                  <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700 }}>
                    Activity Logs
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    User activity tracking and monitoring
                  </Typography>
                </Box>
              </Box>
              <Chip 
                label="Coming Soon" 
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  backdropFilter: 'blur(10px)'
                }} 
              />
            </Box>
          </Container>
        </Paper>

        <Container maxWidth="md" sx={{ mt: 12 }}>
          <Paper
            sx={{
              p: 6,
              textAlign: 'center',
              background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #1d5244 0%, #2e7d32 100%)',
              }
            }}
          >
            {/* Floating Icons Background */}
            <Box sx={{ position: 'absolute', top: 20, left: 20, opacity: 0.1 }}>
              <FloatingIcon delay={0}>
                <Timeline sx={{ fontSize: 40, color: 'primary.main' }} />
              </FloatingIcon>
            </Box>
            <Box sx={{ position: 'absolute', top: 40, right: 30, opacity: 0.1 }}>
              <FloatingIcon delay={1}>
                <NotificationsActive sx={{ fontSize: 35, color: 'secondary.main' }} />
              </FloatingIcon>
            </Box>
            <Box sx={{ position: 'absolute', bottom: 30, left: 40, opacity: 0.1 }}>
              <FloatingIcon delay={2}>
                <Schedule sx={{ fontSize: 30, color: 'primary.light' }} />
              </FloatingIcon>
            </Box>

            {/* Main Content */}
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Icon */}
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  bgcolor: 'primary.main',
                  margin: '0 auto 3rem auto',
                  boxShadow: '0 8px 32px rgba(29, 82, 68, 0.3)',
                }}
              >
                <Construction sx={{ fontSize: 60 }} />
              </Avatar>

              {/* Title */}
              <Typography 
                variant={isMobile ? "h4" : "h3"} 
                sx={{ 
                  mb: 2, 
                  color: 'primary.main',
                  textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                Coming Soon!
              </Typography>

              {/* Subtitle */}
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 3, 
                  color: 'text.secondary',
                  maxWidth: 500,
                  margin: '0 auto 2rem auto'
                }}
              >
                Activity Logs Feature Under Development
              </Typography>

              {/* Description */}
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 4, 
                  color: 'text.secondary',
                  lineHeight: 1.6,
                  maxWidth: 600,
                  margin: '0 auto 3rem auto'
                }}
              >
                We're working hard to bring you a comprehensive activity logging system 
                that will track user interactions, system events, and provide detailed 
                analytics for better insights into user behavior and system performance.
              </Typography>

              {/* Features List */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                  Features Coming Soon:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {[
                    'User Login Tracking',
                    'Search History',
                    'Order Activities',
                    'System Events',
                    'Error Logging',
                    'Performance Metrics'
                  ].map((feature, index) => (
                    <Chip
                      key={index}
                      label={feature}
                      variant="outlined"
                      color="primary"
                      sx={{ 
                        fontWeight: 500,
                        '&:hover': {
                          bgcolor: 'primary.light',
                          color: 'white',
                        }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
